/**
 * VehicleInventoryScanPanel — Fahrzeuginventur (Jun-2026, User-Wunsch).
 *
 * Workflow:
 *   1) Code scannen / eingeben (FIN / interne Nr. / Kennzeichen) → Fahrzeug laden
 *   2) Fahrzeug-Vorabinfo prüfen (Marke/Modell/Farbe/Stellplatz/zuletzt gezählt)
 *   3) Standort eingeben (Pflicht, vorbelegt mit erwartetem Stellplatz)
 *   4) FIN-Foto aufnehmen (Pflicht) + optionale Fotos (z. B. wenn Inserat ohne Bilder)
 *   5) Zählung buchen → Anomalie-Erkennung (falscher Standort / verkauft / keine Bilder)
 */
import { useState } from "react";
import { Loader2, ScanLine, Camera, X, Check, MapPin, AlertTriangle, ImageOff, Car, Plus, FileText } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import PhotoThumb from "./PhotoThumb";

const MAX_EXTRA = 5;
const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString("de-DE"); } catch { return iso; } };

const ANOMALY_META = {
    wrong_location: { label: "Falscher Standort", color: "bg-red-100 text-red-800 border-red-300" },
    sold: { label: "Bereits verkauft", color: "bg-rose-100 text-rose-800 border-rose-300" },
    no_images: { label: "Keine Bilder", color: "bg-amber-100 text-amber-800 border-amber-300" },
};

export default function VehicleInventoryScanPanel({ feed, onCounted, prefillCode }) {
    const [code, setCode] = useState(prefillCode || "");
    const [loading, setLoading] = useState(false);
    const [vehicle, setVehicle] = useState(null);
    const [location, setLocation] = useState("");
    const [vinPhoto, setVinPhoto] = useState(null); // {path, content_type}
    const [extraPhotos, setExtraPhotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [note, setNote] = useState("");
    const [booking, setBooking] = useState(false);
    const token = sessionStorage.getItem("tw_token") || "";

    const reset = () => {
        setVehicle(null); setLocation(""); setVinPhoto(null); setExtraPhotos([]); setNote(""); setCode("");
    };

    const lookup = async (q) => {
        const term = (q ?? code).trim();
        if (term.length < 2) { toast.error("Bitte Code / FIN / Nr. eingeben"); return; }
        setLoading(true);
        try {
            const r = await api.get("/dealer/vehicle-inventory/lookup", { params: { code: term } });
            setVehicle(r.data);
            setLocation(r.data.expected_location || "");
            setVinPhoto(null); setExtraPhotos([]); setNote("");
            toast.success(`${r.data.brand || ""} ${r.data.model || ""} geladen`);
        } catch (e) {
            setVehicle(null);
            toast.error(e.response?.data?.detail || "Fahrzeug nicht gefunden");
        } finally { setLoading(false); }
    };

    const uploadPhoto = async (file, target) => {
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/wms/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const p = { path: r.data.path, content_type: r.data.content_type };
            if (target === "vin") setVinPhoto(p);
            else setExtraPhotos((arr) => [...arr, p].slice(0, MAX_EXTRA));
            toast.success("Foto hochgeladen");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
        } finally { setUploading(false); }
    };

    const onVinPhotoChange = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadPhoto(f, "vin"); };
    const onExtraPhotoChange = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadPhoto(f, "extra"); };

    const locationMismatch = vehicle?.expected_location && location.trim()
        && vehicle.expected_location.trim().toLowerCase() !== location.trim().toLowerCase();

    const [papersBusy, setPapersBusy] = useState(false);
    const bookPapers = async () => {
        if (!vehicle?.papers_inventory_code) return;
        setPapersBusy(true);
        try {
            const r = await api.post(`/listings/${vehicle.id}/dispo/papers-inventory/scan`, { code: vehicle.papers_inventory_code });
            toast.success("Papierzählung gebucht ✓");
            setVehicle((v) => ({ ...v, papers_inventory_last_counted_at: r.data.last_counted_at }));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Papierzählung fehlgeschlagen");
        } finally { setPapersBusy(false); }
    };

    const book = async () => {
        if (!vehicle) return;
        if (!location.trim()) { toast.error("Standort ist Pflicht"); return; }
        if (!vinPhoto) { toast.error("FIN-Foto ist Pflicht"); return; }
        setBooking(true);
        try {
            const r = await api.post("/dealer/vehicle-inventory/count", {
                listing_id: vehicle.id,
                location: location.trim(),
                vin_photo_path: vinPhoto.path,
                extra_photo_paths: extraPhotos.map((p) => p.path),
                note: note.trim(),
            });
            const anomalies = r.data.anomalies || [];
            if (anomalies.length) toast.warning(`Gezählt mit ${anomalies.length} Anomalie(n)`);
            else toast.success(`${vehicle.brand} ${vehicle.model} gezählt ✓`);
            onCounted?.(r.data.count);
            reset();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen");
        } finally { setBooking(false); }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4" data-testid="vehicle-inventory-scan-panel">
            <div className="space-y-4">
                {/* Scan */}
                <div className="bg-white border border-gray-200 rounded-sm p-4 space-y-2">
                    <div className="flex items-center gap-2">
                        <ScanLine className="w-4 h-4 text-[#FFCC00]" />
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Fahrzeug scannen / suchen</Label>
                    </div>
                    <div className="flex gap-2">
                        <Input
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") lookup(); }}
                            placeholder="FIN, interne Nr. oder Kennzeichen scannen / eingeben"
                            className="flex-1 rounded-sm h-9 font-mono-num"
                            data-testid="vehicle-inventory-code-input"
                        />
                        <Button type="button" onClick={() => lookup()} disabled={loading} className="h-9 rounded-sm bg-[#111827] hover:bg-black text-white" data-testid="vehicle-inventory-lookup-btn">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Laden"}
                        </Button>
                    </div>
                </div>

                {/* Fahrzeug-Vorabinfo */}
                {vehicle && (
                    <div className="bg-white border border-gray-200 rounded-sm p-4 space-y-3" data-testid="vehicle-inventory-vehicle-card">
                        <div className="flex items-start gap-3">
                            <div className="w-16 h-16 rounded-sm overflow-hidden border border-gray-200 bg-gray-50 shrink-0 flex items-center justify-center">
                                {vehicle.image ? <PhotoThumb path={vehicle.image} token={token} /> : <Car className="w-7 h-7 text-gray-300" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm">{vehicle.brand} {vehicle.model} {vehicle.year ? `· ${vehicle.year}` : ""}</div>
                                <div className="text-xs text-gray-600 font-mono-num mt-0.5 truncate">{vehicle.internal_number}{vehicle.vin ? ` · FIN ${vehicle.vin}` : ""}{vehicle.color ? ` · ${vehicle.color}` : ""}</div>
                                <div className="text-[11px] text-gray-500 mt-0.5 flex flex-wrap items-center gap-x-2">
                                    {vehicle.expected_location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Erwartet: <strong>{vehicle.expected_location}</strong></span>}
                                    {vehicle.last_counted_at
                                        ? <span data-testid="vehicle-inventory-last-counted">Zuletzt gezählt: {fmtDateTime(vehicle.last_counted_at)}{vehicle.last_counted_by ? ` · ${vehicle.last_counted_by}` : ""}</span>
                                        : <span className="text-amber-600 font-bold" data-testid="vehicle-inventory-never-counted">Noch nie gezählt</span>}
                                </div>
                            </div>
                        </div>
                        {/* Vorab-Anomalie-Hinweise */}
                        <div className="flex flex-wrap gap-1.5">
                            {vehicle.sold && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 border rounded-sm bg-rose-100 text-rose-800 border-rose-300" data-testid="vehicle-inventory-flag-sold"><AlertTriangle className="w-3 h-3" /> Bereits verkauft</span>}
                            {vehicle.image_count === 0 && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 border rounded-sm bg-amber-100 text-amber-800 border-amber-300" data-testid="vehicle-inventory-flag-no-images"><ImageOff className="w-3 h-3" /> Keine Bilder — bitte Fotos hochladen</span>}
                        </div>

                        {/* Jun-2026 (User-Wunsch) — Fahrzeugpapierinventur: Code wird hier
                            erkannt; Zählung per Klick (Datum aktualisieren) ohne Foto-Pflicht. */}
                        {vehicle.papers_inventory_code && (
                            <div className="bg-gray-50 border border-gray-200 rounded-sm p-2.5 flex items-center justify-between gap-2" data-testid="vehicle-inventory-papers">
                                <div className="text-[11px] text-gray-600 min-w-0">
                                    <div className="font-bold uppercase tracking-wider text-gray-500 text-[10px] flex items-center gap-1">
                                        <FileText className="w-3 h-3" /> Fahrzeugpapiere
                                        {vehicle.matched_papers && <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-[#FFCC00] text-[#111827] font-black" data-testid="vehicle-inventory-papers-matched">Code gescannt</span>}
                                    </div>
                                    <div className="font-mono-num truncate text-gray-800">{vehicle.papers_inventory_code}</div>
                                    {vehicle.papers_inventory_last_counted_at
                                        ? <div className="text-gray-400">Letzte Zählung: {fmtDateTime(vehicle.papers_inventory_last_counted_at)}</div>
                                        : <div className="text-amber-600 font-bold">Papiere noch nie gezählt</div>}
                                </div>
                                <Button type="button" onClick={bookPapers} disabled={papersBusy} className="h-8 rounded-sm bg-[#111827] hover:bg-black text-white shrink-0" data-testid="vehicle-inventory-papers-count-btn">
                                    {papersBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" /> Papiere zählen</>}
                                </Button>
                            </div>
                        )}

                        {/* Standort (Pflicht) */}
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Tatsächlicher Standort (Pflicht)</Label>
                            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="z. B. Halle 2 · Box 14" className="rounded-sm mt-1 h-9" data-testid="vehicle-inventory-location-input" />
                            {locationMismatch && (
                                <div className="mt-1 text-[11px] text-red-700 flex items-center gap-1" data-testid="vehicle-inventory-location-mismatch">
                                    <AlertTriangle className="w-3.5 h-3.5" /> Weicht vom erwarteten Stellplatz „{vehicle.expected_location}" ab — wird als Anomalie erfasst.
                                </div>
                            )}
                        </div>

                        {/* FIN-Foto (Pflicht) */}
                        <div>
                            <div className="flex items-center justify-between">
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5"><Camera className="w-3.5 h-3.5 text-[#FFCC00]" /> FIN-Foto (Pflicht)</Label>
                                {!vinPhoto && (
                                    <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border cursor-pointer bg-[#FFCC00] hover:bg-[#FFD633] border-[#FFCC00]" data-testid="vehicle-inventory-vin-photo-label">
                                        <Camera className="w-3.5 h-3.5" /> FIN fotografieren
                                        <input type="file" accept="image/*" capture="environment" className="hidden" disabled={uploading} onChange={onVinPhotoChange} data-testid="vehicle-inventory-vin-photo-input" />
                                    </label>
                                )}
                            </div>
                            {vinPhoto ? (
                                <div className="mt-2 relative w-24 h-24 rounded-sm overflow-hidden border border-gray-200 group" data-testid="vehicle-inventory-vin-photo-thumb">
                                    <PhotoThumb path={vinPhoto.path} token={token} />
                                    <button type="button" onClick={() => setVinPhoto(null)} className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center" title="Entfernen"><X className="w-3.5 h-3.5" /></button>
                                </div>
                            ) : (
                                <div className="mt-1 text-[11px] text-gray-400 italic">Bitte das FIN-/Typenschild fotografieren, um die Zählung zu belegen.</div>
                            )}
                        </div>

                        {/* Optionale Fotos */}
                        <div>
                            <div className="flex items-center justify-between">
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Weitere Fotos ({extraPhotos.length}/{MAX_EXTRA}){vehicle.image_count === 0 ? " — empfohlen, da keine Bilder vorhanden" : ""}</Label>
                                <label className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border cursor-pointer ${extraPhotos.length >= MAX_EXTRA ? "opacity-50 cursor-not-allowed bg-gray-50" : "bg-white hover:bg-gray-50 border-gray-300"}`} data-testid="vehicle-inventory-extra-photo-label">
                                    <Plus className="w-3.5 h-3.5" /> Foto
                                    <input type="file" accept="image/*" capture="environment" className="hidden" disabled={extraPhotos.length >= MAX_EXTRA || uploading} onChange={onExtraPhotoChange} data-testid="vehicle-inventory-extra-photo-input" />
                                </label>
                            </div>
                            {extraPhotos.length > 0 && (
                                <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-2">
                                    {extraPhotos.map((p) => (
                                        <div key={p.path} className="relative aspect-square rounded-sm overflow-hidden border border-gray-200 group">
                                            <PhotoThumb path={p.path} token={token} />
                                            <button type="button" onClick={() => setExtraPhotos((arr) => arr.filter((x) => x.path !== p.path))} className="absolute top-0.5 right-0.5 bg-black/70 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center"><X className="w-3 h-3" /></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Vermerk (optional)</Label>
                            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="z. B. Kratzer am Tank, Schlüssel fehlt …" className="rounded-sm mt-1 h-9" data-testid="vehicle-inventory-note-input" />
                        </div>

                        {uploading && <div className="text-xs text-gray-500"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Foto lädt hoch…</div>}

                        <div className="flex items-center gap-2 justify-end pt-1">
                            <Button type="button" variant="outline" onClick={reset} className="rounded-sm" data-testid="vehicle-inventory-reset-btn">Abbrechen</Button>
                            <Button type="button" onClick={book} disabled={booking || !location.trim() || !vinPhoto} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="vehicle-inventory-book-btn">
                                {booking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bucht…</> : <><Check className="w-4 h-4 mr-2" /> Zählung buchen</>}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Live-Feed */}
            <aside className="bg-gray-50 border border-gray-200 rounded-sm p-3 space-y-2 max-h-[640px] overflow-y-auto" data-testid="vehicle-inventory-feed">
                <div className="flex items-center gap-2 sticky top-0 bg-gray-50 pb-2">
                    <ScanLine className="w-4 h-4 text-[#FFCC00]" />
                    <h3 className="font-bold text-sm">Letzte Zählungen</h3>
                    <span className="ml-auto text-[10px] text-gray-500 font-mono-num">{(feed || []).length}</span>
                </div>
                {(feed || []).length === 0 && <div className="text-xs text-gray-400 italic py-4 text-center">Noch keine Zählungen.</div>}
                {(feed || []).map((f) => (
                    <div key={f.id} className="bg-white border border-gray-200 rounded-sm p-2 text-xs space-y-1" data-testid={`vehicle-inventory-feed-${f.id}`}>
                        <div className="font-bold truncate">{f.brand} {f.model}</div>
                        <div className="text-gray-500 font-mono-num text-[10px] truncate">{f.internal_number} · {f.location}</div>
                        {(f.anomalies || []).length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {f.anomalies.map((a, i) => (
                                    <span key={i} className={`text-[9px] font-bold px-1.5 py-0.5 border rounded-sm ${ANOMALY_META[a.type]?.color || "bg-gray-100 text-gray-700 border-gray-200"}`}>{ANOMALY_META[a.type]?.label || a.type}</span>
                                ))}
                            </div>
                        )}
                        <div className="text-[10px] text-gray-400">{fmtDateTime(f.counted_at)} · {f.counted_by_name}</div>
                    </div>
                ))}
            </aside>
        </div>
    );
}
